import axios from 'axios';
import type {
  DashboardStats,
  Activity,
  Adresse,
  AdresseDetail,
  CreateAdresseDto,
  Email,
  EmailAnhang,
  MailKonto,
  CreateMailKontoDto,
  EmailSignatur,
  CreateSignaturDto,
  Vorlage,
  VorlageContent,
  Textbaustein,
  TextbausteinContent,
  GeneriertesDokument,
  GenerateDokumentRequest,
  LLMProvidersResponse,
  GenerateTextRequest,
  LLMResponse,
  GenerateEmailReplyRequest,
  EmailReplySuggestionsResponse,
  AnalyzeEmailRequest,
  EmailAnalysisResponse,
  AnalyzeEmailForTicketRequest,
  TicketAnalysisResponse,
  Einstellung,
  UpdateEinstellungRequest,
  Ticket,
  CreateTicketRequest,
  UpdateTicketRequest,
  UpdateTicketStatusRequest,
  TicketStats,
  SendAppointmentRequest,
  TicketDocument,
  TicketEmail,
  Mandant,
  CreateMandantDto,
  UpdateMandantDto,
  MandantStats,
  Gruppe,
  Benutzer,
  UpdateBenutzerDto,
  CreateBenutzerDto,
  ResetPasswortDto,
  AssignMandantDto,
  AssignMailKontoDto,
  Rolle,
  Berechtigung,
  BerechtigungenGruppiert,
  SetBenutzerRollenDto,
  CreateRolleDto,
  UpdateRolleDto,
  PaperlessDocumentType,
  PaperlessTag,
  ArchiveDocumentRequest,
  ArchiveDocumentResponse,
  PaperlessDocument,
  PaperlessDocumentsResponse,
  GetPaperlessDocumentsRequest,
  UploadToAnythingLLMRequest,
  UploadToAnythingLLMResponse,
  Webhook,
  CreateWebhookDto,
  UpdateWebhookDto,
  WebhookLog,
  WebhookExecutionRequest,
  WebhookExecutionResult,
  Frist,
  CreateFristDto,
  UpdateFristDto,
  FristStatistik,
  Zeiterfassung,
  CreateZeiterfassungDto,
  UpdateZeiterfassungDto,
  ZeiterfassungStatistik,
  ZeiterfassungFilter,
  Abrechnung,
  CreateAbrechnungDto,
  UpdateAbrechnungDto,
  AbrechnungStatistik,
  AbrechnungFilter,
  NaechsteRechnungsnummer,
  SendRechnungEmailDto,
} from '../types/api';

// Dynamically determine API base URL based on current host
const getApiBaseUrl = () => {
  const hostname = window.location.hostname;

  // Entwicklung: localhost verwendet direkten Backend-Zugriff
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:5000/api';
  }

  // Produktion: Alle Zugriffe laufen über NGINX Proxy
  // Die API wird unter /api bereitgestellt (keine Port-Angabe nötig)
  // Verwendet automatisch das gleiche Protokoll (http/https) wie die Seite
  return `${window.location.protocol}//${hostname}/api`;
};

const API_BASE_URL = getApiBaseUrl();

// Custom params serializer for ASP.NET Core compatibility
// Converts arrays to repeated key format: tagIds=1&tagIds=2 instead of tagIds[0]=1&tagIds[1]=2
const paramsSerializer = (params: Record<string, any>): string => {
  const parts: string[] = [];
  for (const key in params) {
    const value = params[key];
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      value.forEach((v) => {
        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(v)}`);
      });
    } else {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    }
  }
  return parts.join('&');
};

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer: paramsSerializer,
});

// Token-Refresh Management
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: unknown) => void;
  reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Token Storage Keys (müssen mit authService übereinstimmen)
const TOKEN_KEY = 'skeena_token';
const REFRESH_TOKEN_KEY = 'skeena_refresh_token';
const USER_KEY = 'skeena_user';

// Funktion zum Refresh des Tokens
const refreshAccessToken = async (): Promise<string | null> => {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!refreshToken) {
    return null;
  }

  try {
    const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
      refreshToken: refreshToken,
    });

    if (response.data && response.data.accessToken) {
      localStorage.setItem(TOKEN_KEY, response.data.accessToken);
      if (response.data.refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, response.data.refreshToken);
      }
      // Auch den Authorization Header aktualisieren
      api.defaults.headers.common['Authorization'] = `Bearer ${response.data.accessToken}`;
      return response.data.accessToken;
    }
    return null;
  } catch {
    return null;
  }
};

// Proaktives Token-Refresh bei Aktivität (alle 30 Minuten)
let lastActivityRefresh = Date.now();
const REFRESH_INTERVAL = 30 * 60 * 1000; // 30 Minuten

const checkAndRefreshToken = async () => {
  const now = Date.now();
  const token = localStorage.getItem(TOKEN_KEY);
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

  if (token && refreshToken && now - lastActivityRefresh > REFRESH_INTERVAL) {
    lastActivityRefresh = now;
    await refreshAccessToken();
  }
};

// Bei Benutzeraktivität Token proaktiv refreshen
if (typeof window !== 'undefined') {
  ['click', 'keypress', 'scroll', 'mousemove'].forEach((event) => {
    window.addEventListener(event, () => {
      checkAndRefreshToken();
    }, { passive: true });
  });
}

// Response interceptor für automatisches Token-Refresh bei 401
api.interceptors.response.use(
  (response) => {
    // Erfolgreiche Responses einfach durchreichen
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Prüfen ob 401 Unauthorized und noch kein Retry
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Nicht auf Login-Seite und nicht bereits beim Refresh-Endpoint
      if (window.location.pathname.includes('/login') ||
          originalRequest.url?.includes('/auth/refresh')) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Warte auf laufenden Refresh
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers['Authorization'] = 'Bearer ' + token;
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await refreshAccessToken();

        if (newToken) {
          // Token erfolgreich erneuert
          processQueue(null, newToken);
          originalRequest.headers['Authorization'] = 'Bearer ' + newToken;
          return api(originalRequest);
        } else {
          // Refresh fehlgeschlagen - zum Login umleiten
          processQueue(error, null);
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(REFRESH_TOKEN_KEY);
          localStorage.removeItem(USER_KEY);

          const currentPath = window.location.pathname + window.location.search;
          if (currentPath !== '/' && currentPath !== '/login') {
            sessionStorage.setItem('redirectAfterLogin', currentPath);
          }
          window.location.href = '/login';
        }
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Fehler weiter durchreichen für weitere Fehlerbehandlung
    return Promise.reject(error);
  }
);

// Dashboard API
export const dashboardApi = {
  getStats: () => api.get<DashboardStats>('/dashboard/stats'),
  getActivities: (limit: number = 10) =>
    api.get<Activity[]>(`/dashboard/activities?limit=${limit}`),
};

// Adressen API
export const adressenApi = {
  getAll: (params?: { search?: string; skip?: number; take?: number }) =>
    api.get<Adresse[]>('/adressen', { params }),
  getById: (id: number) => api.get<Adresse>(`/adressen/${id}`),
  getDetails: (id: number) => api.get<AdresseDetail>(`/adressen/${id}/details`),
  create: (data: CreateAdresseDto) => api.post<Adresse>('/adressen', data),
  update: (id: number, data: CreateAdresseDto) =>
    api.put<Adresse>(`/adressen/${id}`, data),
  delete: (id: number) => api.delete(`/adressen/${id}`),
  // Mandanten-spezifische Endpoints
  getMandanten: (params?: { search?: string }) =>
    api.get<Adresse[]>('/adressen/mandanten', { params }),
  getMandantStatistik: (id: number) =>
    api.get<{ anzahlEmails: number; anzahlDokumente: number; anzahlTicketsOffen: number; anzahlTicketsErledigt: number }>(`/adressen/mandanten/${id}/statistik`),
  getHistorie: (id: number, limit?: number) =>
    api.get<Array<{ typ: string; id: number; titel: string; richtung: string | null; datum: string; beschreibung: string | null }>>(`/adressen/${id}/historie`, { params: { limit } }),
};

// Emails API
export const emailsApi = {
  getAll: (params?: {
    kontoId?: number;
    gelesen?: boolean;
    skip?: number;
    take?: number;
  }) => api.get<Email[]>('/emails', { params }),
  getById: (id: number) => api.get<Email>(`/emails/${id}`),
  getAttachments: (id: number) =>
    api.get<EmailAnhang[]>(`/emails/${id}/attachments`),
  downloadAttachment: (anhangId: number) =>
    api.get(`/emails/attachments/${anhangId}/download`, { responseType: 'blob' }),
  markAsRead: (id: number, read: boolean = true) =>
    api.put(`/emails/${id}/mark-read?read=${read}`),
  markAsAnswered: (id: number) => api.put(`/emails/${id}/mark-answered`),
  sendReply: (id: number, data: { antwortText: string; antwortHtml?: string; signaturID?: number; cc?: string } | FormData) => {
    // Prüfen, ob FormData verwendet wird (für Anhänge)
    if (data instanceof FormData) {
      return api.post(`/emails/${id}/reply`, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    }
    // Konvertiere JSON-Objekt zu FormData (Backend erwartet [FromForm])
    const formData = new FormData();
    formData.append('antwortText', data.antwortText);
    if (data.antwortHtml) {
      formData.append('antwortHtml', data.antwortHtml);
    }
    if (data.signaturID !== undefined) {
      formData.append('signaturID', data.signaturID.toString());
    }
    if (data.cc) {
      formData.append('cc', data.cc);
    }
    return api.post(`/emails/${id}/reply`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  forward: (id: number, data: { empfaenger: string; nachricht?: string; cc?: string; bcc?: string; signaturID?: number }) =>
    api.post(`/emails/${id}/forward`, data),
  sendNew: (data: {
    kontoID?: number;
    empfaenger: string;
    betreff: string;
    textInhalt: string;
    htmlInhalt?: string;
    cc?: string;
    bcc?: string;
    signaturID?: number
  }) =>
    api.post(`/emails/send`, data),
  sendNewWithAttachments: (formData: FormData) =>
    api.post(`/emails/send-with-attachments`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
  delete: (id: number) => api.delete(`/emails/${id}`),
  // KI-gestützte Funktionen
  suggestReply: (id: number, data?: { zusaetzlicherKontext?: string }) =>
    api.post(`/emails/${id}/suggest-reply`, data || {}),
  extractAppointments: (id: number, data?: { calDAVSettingID?: number; autoCreateEvents?: boolean }) =>
    api.post(`/emails/${id}/extract-appointments`, data || {}),
  // Adress-Zuordnung
  assignAddress: (id: number, adresseID: number | null) =>
    api.put(`/emails/${id}/assign-address`, { adresseID }),
};

// Mail-Konten API
export const mailKontenApi = {
  getAll: () => api.get<MailKonto[]>('/mailkonten'),
  getById: (id: number) => api.get<MailKonto>(`/mailkonten/${id}`),
  getStandard: () => api.get<MailKonto>('/mailkonten/standard'),
  create: (data: CreateMailKontoDto) => api.post<MailKonto>('/mailkonten', data),
  toggleActive: (id: number, active: boolean) =>
    api.put(`/mailkonten/${id}/toggle-active?active=${active}`),
  setStandard: (id: number) => api.put(`/mailkonten/${id}/set-standard`),
  fetchEmails: (id: number, maxCount: number = 50) =>
    api.post(`/mailkonten/${id}/fetch-emails?maxCount=${maxCount}`),
  fetchAllEmails: (maxCount: number = 50) =>
    api.post(`/mailkonten/fetch-all-emails?maxCount=${maxCount}`),
  delete: (id: number) => api.delete(`/mailkonten/${id}`),
};

// Microsoft 365 Auth API
export const m365AuthApi = {
  startOAuthFlow: (data?: { clientId?: string; redirectUri?: string; tenantId?: string }) =>
    api.post<{ authorizationUrl: string; state: string; expiresIn: number; configSource: string }>('/M365Auth/start', data || {}),
  refreshToken: (kontoId: number) =>
    api.post(`/M365Auth/${kontoId}/refresh`),
  testConnection: (kontoId: number) =>
    api.post(`/M365Auth/${kontoId}/test`),
};

// Microsoft 365 Config API (Admin only)
export const m365ConfigApi = {
  getCurrent: () =>
    api.get<{ clientId: string; hasClientSecret: boolean; tenantId: string; redirectUri: string; appName: string; source: string; isConfigured: boolean }>('/M365Config/current'),
  getByMandant: (mandantId: number) =>
    api.get(`/M365Config/${mandantId}`),
  update: (mandantId: number, data: { clientId: string; clientSecret: string; tenantId?: string; redirectUri?: string; appName?: string; aktiv: boolean }) =>
    api.put(`/M365Config/${mandantId}`, data),
  delete: (mandantId: number) =>
    api.delete(`/M365Config/${mandantId}`),
  test: (mandantId: number) =>
    api.post(`/M365Config/${mandantId}/test`),
  getSetupGuide: () =>
    api.get<{ steps: string[]; requiredPermissions: string[]; documentationUrl: string }>('/M365Config/setup-guide'),
};

// Signaturen API
export const signaturenApi = {
  getAll: () => api.get<EmailSignatur[]>('/signaturen'),
  getById: (id: number) => api.get<EmailSignatur>(`/signaturen/${id}`),
  getStandard: () => api.get<EmailSignatur>('/signaturen/standard'),
  create: (data: CreateSignaturDto) =>
    api.post<EmailSignatur>('/signaturen', data),
  update: (id: number, data: CreateSignaturDto) =>
    api.put<EmailSignatur>(`/signaturen/${id}`, data),
  setStandard: (id: number) => api.put(`/signaturen/${id}/set-standard`),
  delete: (id: number) => api.delete(`/signaturen/${id}`),
};

// Vorlagen API
export const vorlagenApi = {
  getAll: (params?: { typ?: string; search?: string }) =>
    api.get<Vorlage[]>('/vorlagen', { params }),
  getById: (id: number) => api.get<Vorlage>(`/vorlagen/${id}`),
  getContent: (id: number) =>
    api.get<VorlageContent>(`/vorlagen/${id}/content`),
  create: (data: { vorlageName: string; vorlageTyp: string; dateiPfad?: string; dateiFormat?: string; beschreibung?: string; kategorie?: string; mandantID?: number; gruppeID?: number }) =>
    api.post<Vorlage>('/vorlagen', data),
  upload: (file: File, vorlageName: string, vorlageTyp: string, beschreibung?: string, kategorie?: string, mandantID?: number, gruppeID?: number) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('vorlageName', vorlageName);
    formData.append('vorlageTyp', vorlageTyp);
    if (beschreibung) formData.append('beschreibung', beschreibung);
    if (kategorie) formData.append('kategorie', kategorie);
    if (mandantID) formData.append('mandantID', mandantID.toString());
    if (gruppeID) formData.append('gruppeID', gruppeID.toString());
    return api.post<Vorlage>('/vorlagen/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  update: (id: number, data: { vorlageName: string; vorlageTyp: string; dateiPfad?: string; dateiFormat?: string; beschreibung?: string; kategorie?: string; mandantID?: number; gruppeID?: number }) =>
    api.put<Vorlage>(`/vorlagen/${id}`, data),
  delete: (id: number) => api.delete(`/vorlagen/${id}`),
};

// Textbausteine API
export const textbausteineApi = {
  getAll: (params?: { kategorie?: string; search?: string }) =>
    api.get<Textbaustein[]>('/textbausteine', { params }),
  getById: (id: number) => api.get<Textbaustein>(`/textbausteine/${id}`),
  getContent: (id: number) =>
    api.get<TextbausteinContent>(`/textbausteine/${id}/content`),
  getKategorien: () => api.get<string[]>('/textbausteine/kategorien'),
  create: (data: { bausteinName: string; kategorie: string; kurzerText?: string; tags?: string; kiVerbessern?: boolean; mandantID?: number; gruppeID?: number }) =>
    api.post<Textbaustein>('/textbausteine', data),
  update: (id: number, data: { bausteinName: string; kategorie: string; kurzerText?: string; tags?: string; kiVerbessern?: boolean }) =>
    api.put<Textbaustein>(`/textbausteine/${id}`, data),
  updateContent: (id: number, data: { content: string }) =>
    api.put(`/textbausteine/${id}/content`, data),
  delete: (id: number) => api.delete(`/textbausteine/${id}`),
  enhanceWithAI: (id: number, workspaceSlug?: string) =>
    api.post<TextbausteinContent>(`/textbausteine/${id}/ai-enhance`, null, {
      params: workspaceSlug ? { workspaceSlug } : undefined
    }),
};

// Dokumente API
export const dokumenteApi = {
  getAll: (params?: {
    typ?: string;
    status?: string;
    search?: string;
    skip?: number;
    take?: number;
  }) => api.get<GeneriertesDokument[]>('/dokumente', { params }),
  getById: (id: number) => api.get<GeneriertesDokument>(`/dokumente/${id}`),
  generate: (data: GenerateDokumentRequest) =>
    api.post<GeneriertesDokument>('/dokumente/generate', data),
  upload: (file: File, betreff?: string, typ?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    if (betreff) {
      formData.append('betreff', betreff);
    }
    if (typ) {
      formData.append('typ', typ);
    }
    return api.post<GeneriertesDokument>('/dokumente/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  updateStatus: (
    id: number,
    data: { status: string; versendetAm?: string }
  ) => api.put(`/dokumente/${id}/status`, data),
  updateContent: (
    id: number,
    data: { betreff?: string; inhalt?: string; adresseID?: number; fusszeile?: string; skipFileRegeneration?: boolean }
  ) => api.put(`/dokumente/${id}/content`, data),
  preview: (id: number) => api.get<string>(`/dokumente/${id}/preview`),
  download: (id: number, format?: string) =>
    api.get(`/dokumente/${id}/download`, {
      responseType: 'blob',
      params: format ? { format } : undefined
    }),
  delete: (id: number) => api.delete(`/dokumente/${id}`),
  updateFile: (id: number, formData: FormData) =>
    api.put(`/dokumente/${id}/file`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
  addSignature: (
    id: number,
    data: {
      signatureBase64: string;
      pageNumber?: number;
      x?: number;
      y?: number;
      width?: number;
      height?: number;
    }
  ) => api.post(`/dokumente/${id}/add-signature`, data),
  assignAddress: (id: number, adresseID: number | null) =>
    api.put(`/dokumente/${id}/assign-address`, { adresseID }),
};

// LLM API
export const llmApi = {
  getProviders: () => api.get<LLMProvidersResponse>('/llm/providers'),
  generate: (data: GenerateTextRequest) =>
    api.post<LLMResponse>('/llm/generate', data),
  generateEmailReply: (data: GenerateEmailReplyRequest) =>
    api.post<EmailReplySuggestionsResponse>('/llm/generate-email-reply', data),
  analyzeEmail: (data: AnalyzeEmailRequest) =>
    api.post<EmailAnalysisResponse>('/llm/analyze-email', data),
  analyzeEmailForTicket: (data: AnalyzeEmailForTicketRequest) =>
    api.post<TicketAnalysisResponse>('/llm/analyze-email-for-ticket', data),
  improveText: (data: {
    originalText: string;
    verbesserungsziel?: string;
    empfaengerAnrede?: string;
    empfaengerTitel?: string;
    empfaengerVorname?: string;
    empfaengerNachname?: string;
    empfaengerFirma?: string;
  }) => api.post<LLMResponse>('/llm/improve-text', data),
  summarize: (data: { text: string; maxSaetze?: number }) =>
    api.post<LLMResponse>('/llm/summarize', data),
};

// Einstellungen API
export const einstellungenApi = {
  getAll: () => api.get<Einstellung[]>('/einstellungen'),
  getByKey: (schluessel: string) => api.get<Einstellung>(`/einstellungen/${schluessel}`),
  update: (schluessel: string, data: UpdateEinstellungRequest) =>
    api.put(`/einstellungen/${schluessel}`, data),
};

// Tickets API
export const ticketsApi = {
  getAll: (params?: {
    status?: string;
    prioritaet?: string;
    emailId?: number;
    adresseId?: number;
    skip?: number;
    take?: number;
  }) => api.get<Ticket[]>('/tickets', { params }),
  getById: (id: number) => api.get<Ticket>(`/tickets/${id}`),
  create: (data: CreateTicketRequest) => api.post<Ticket>('/tickets', data),
  update: (id: number, data: UpdateTicketRequest) =>
    api.put<Ticket>(`/tickets/${id}`, data),
  updateStatus: (id: number, data: UpdateTicketStatusRequest) =>
    api.patch<Ticket>(`/tickets/${id}/status`, data),
  delete: (id: number) => api.delete(`/tickets/${id}`),
  getStats: () => api.get<TicketStats>('/tickets/stats'),
  sendAppointment: (id: number, data: SendAppointmentRequest) =>
    api.post(`/tickets/${id}/send-appointment`, data),

  // Dokumente
  getDocuments: (ticketId: number) =>
    api.get<TicketDocument[]>(`/tickets/${ticketId}/documents`),
  linkDocument: (ticketId: number, dokumentId: number) =>
    api.post(`/tickets/${ticketId}/documents/link`, { dokumentID: dokumentId }),
  unlinkDocument: (ticketId: number, dokumentId: number) =>
    api.delete(`/tickets/${ticketId}/documents/${dokumentId}`),

  // E-Mail
  getEmail: (ticketId: number) =>
    api.get<TicketEmail>(`/tickets/${ticketId}/email`),
  linkEmail: (ticketId: number, emailId: number) =>
    api.post(`/tickets/${ticketId}/email/link`, { emailID: emailId }),
  unlinkEmail: (ticketId: number) =>
    api.delete(`/tickets/${ticketId}/email`),
};

// Mandanten API (Admin)
export const mandantenApi = {
  getAll: () => api.get<Mandant[]>('/mandanten'),
  getById: (id: number) => api.get<Mandant>(`/mandanten/${id}`),
  create: (data: CreateMandantDto) => api.post<Mandant>('/mandanten', data),
  update: (id: number, data: UpdateMandantDto) =>
    api.put<Mandant>(`/mandanten/${id}`, data),
  delete: (id: number) => api.delete(`/mandanten/${id}`),
  getStats: (id: number) => api.get<MandantStats>(`/mandanten/${id}/stats`),
};

// Gruppen API
export const gruppenApi = {
  getAll: (params?: { mandantId?: number }) =>
    api.get<Gruppe[]>('/gruppen', { params }),
};

// Benutzer API (Admin)
export const benutzerApi = {
  getAll: (params?: { mandantId?: number; status?: string }) =>
    api.get<Benutzer[]>('/benutzer', { params }),
  getMandantBenutzer: () =>
    api.get<Benutzer[]>('/benutzer/mandant'), // Nur Benutzer des eigenen Mandanten
  getById: (id: number) => api.get<Benutzer>(`/benutzer/${id}`),
  create: (data: CreateBenutzerDto) =>
    api.post<Benutzer>('/benutzer', data),
  update: (id: number, data: UpdateBenutzerDto) =>
    api.put<Benutzer>(`/benutzer/${id}`, data),
  resetPassword: (id: number, data: ResetPasswortDto) =>
    api.put(`/benutzer/${id}/passwort-reset`, data),
  activate: (id: number) =>
    api.put(`/benutzer/${id}/activate`),
  assignMandant: (id: number, data: AssignMandantDto) =>
    api.put(`/benutzer/${id}/mandant`, data),
  assignMailKonto: (id: number, data: AssignMailKontoDto) =>
    api.put(`/benutzer/${id}/mailkonto`, data),
  delete: (id: number) => api.delete(`/benutzer/${id}`),
  uploadUnterschrift: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/benutzer/unterschrift', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getUnterschrift: () =>
    api.get('/benutzer/unterschrift', { responseType: 'blob' }),
  deleteUnterschrift: () =>
    api.delete('/benutzer/unterschrift'),
};

// Rollen API
export const rollenApi = {
  getAll: () => api.get<Rolle[]>('/rollen'),
  getById: (id: number) => api.get<Rolle>(`/rollen/${id}`),
  create: (data: CreateRolleDto) => api.post<Rolle>('/rollen', data),
  update: (id: number, data: UpdateRolleDto) => api.put<Rolle>(`/rollen/${id}`, data),
  delete: (id: number) => api.delete(`/rollen/${id}`),
  getBerechtigungen: () => api.get<BerechtigungenGruppiert[]>('/rollen/berechtigungen'),
  getBenutzerRollen: (benutzerId: number) => api.get<Rolle[]>(`/rollen/benutzer/${benutzerId}`),
  setBenutzerRollen: (benutzerId: number, data: SetBenutzerRollenDto) =>
    api.put(`/rollen/benutzer/${benutzerId}`, data),
  addBenutzerRolle: (benutzerId: number, rolleId: number) =>
    api.post(`/rollen/benutzer/${benutzerId}/rolle/${rolleId}`),
  removeBenutzerRolle: (benutzerId: number, rolleId: number) =>
    api.delete(`/rollen/benutzer/${benutzerId}/rolle/${rolleId}`),
};

// Auth API (Profile)
export const authApi = {
  updateProfile: (data: { standardOrt?: string; paperlessUrl?: string; paperlessUsername?: string; paperlessToken?: string }) =>
    api.put('/auth/me', data),
};

// Paperless-NGX API
export const paperlessApi = {
  getDocumentTypes: () =>
    api.get<PaperlessDocumentType[]>('/paperless/document-types'),
  getTags: () =>
    api.get<PaperlessTag[]>('/paperless/tags'),
  archiveDocument: (data: ArchiveDocumentRequest) =>
    api.post<ArchiveDocumentResponse>('/paperless/archive', data),
  getDocuments: (params?: GetPaperlessDocumentsRequest) =>
    api.get<PaperlessDocumentsResponse>('/paperless/documents', { params }),
  getDocument: (id: number) =>
    api.get<PaperlessDocument>(`/paperless/documents/${id}`),
  downloadDocument: (id: number, original: boolean = false) =>
    api.get(`/paperless/documents/${id}/download`, {
      responseType: 'blob',
      params: { original }
    }),
  uploadToAnythingLLM: (data: UploadToAnythingLLMRequest) =>
    api.post<UploadToAnythingLLMResponse>('/paperless/upload-to-llm', data),
  previewDocument: (id: number, original: boolean = false) =>
    api.get(`/paperless/documents/${id}/preview`, {
      responseType: 'blob',
      params: { original }
    }),
};

// Webhook API für N8N Integration
export const webhooksApi = {
  getAll: () => api.get<Webhook[]>('/webhooks'),
  getById: (id: number) => api.get<Webhook>(`/webhooks/${id}`),
  create: (data: CreateWebhookDto) => api.post<Webhook>('/webhooks', data),
  update: (id: number, data: UpdateWebhookDto) => api.put<Webhook>(`/webhooks/${id}`, data),
  delete: (id: number) => api.delete(`/webhooks/${id}`),
  test: (id: number) => api.post<WebhookExecutionResult>(`/webhooks/${id}/test`, {}),
  execute: (id: number, data: WebhookExecutionRequest) => api.post<WebhookExecutionResult>(`/webhooks/${id}/execute`, data),
  getLogs: (id: number, limit?: number) => api.get<WebhookLog[]>(`/webhooks/${id}/logs${limit ? `?limit=${limit}` : ''}`),
};

// System Prompts API für LLM-Verwaltung
export const systemPromptsApi = {
  getAll: () => api.get<any[]>('/SystemPrompts'),
  getById: (key: string) => api.get<any>(`/SystemPrompts/${key}`),
  update: (key: string, data: { promptValue: string }) => api.put(`/SystemPrompts/${key}`, data),
  reset: (key: string) => api.delete(`/SystemPrompts/${key}`),
};

// CalDAV API für Kalender-Integration
export interface CalDAVSetting {
  calDAVSettingID: number;
  name: string;
  beschreibung?: string;
  serverUrl: string;
  username: string;
  calendarPath: string;
  syncIntervalMinutes: number;
  aktiv: boolean;
  lastSyncAt?: string;
  erstelltAm: string;
  aktualiziertAm?: string;
  mandantID?: number;
}

export interface CreateCalDAVEventRequest {
  CalDAVSettingID: number;
  Summary: string;
  Start: string;
  End: string;
  Description?: string;
  Location?: string;
  AllDay?: boolean;
}

export const calDAVApi = {
  getSettings: () => api.get<CalDAVSetting[]>('/caldav/settings'),
  createEvent: (data: CreateCalDAVEventRequest) =>
    api.post<{ success: boolean; eventUID?: string; message: string }>('/caldav/events', data),
};

// AI Chat API
export interface AIChatResponse {
  success: boolean;
  messageType: string;
  message: string;
  data?: any;
}

export const aiChatApi = {
  sendMessage: (message: string) =>
    api.post<AIChatResponse>('/aichat/message', { message }),
  getCapabilities: () => api.get('/aichat/capabilities'),
  getSuggestions: () => api.get('/aichat/suggestions'),
};

// Speech-to-Text API (Whisper)
export interface SpeechTranscriptionResponse {
  success: boolean;
  text: string;
  language: string;
  confidence: number;
  duration: number;
}

export interface SpeechHealthResponse {
  status: string;
  whisperService?: {
    status: string;
    model: string;
    device: string;
  };
  message?: string;
}

export const speechApi = {
  transcribe: (audioBlob: Blob, language: string = 'de') => {
    const formData = new FormData();
    formData.append('file', audioBlob, 'recording.webm');
    return api.post<SpeechTranscriptionResponse>(`/speech/transcribe?language=${language}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  health: () => api.get<SpeechHealthResponse>('/speech/health'),
};

// ScheduledJobs API für Job-Verwaltung
export interface ScheduledJob {
  jobId: number;
  mandantId?: number;
  jobName: string;
  jobTyp: string;
  beschreibung?: string;
  intervallMinuten: number;
  startzeit?: string; // Format "HH:mm" - Uhrzeit für erste Ausführung
  konfiguration?: string;
  istAktiv: boolean;
  letzteAusfuehrung?: string;
  naechsteAusfuehrung?: string;
  letzterFehler?: string;
  fehlerAnzahl: number;
  erstelltAm: string;
  erstelltVon?: number;
  geaendertAm?: string;
  geaendertVon?: number;
}

export interface JobTypeInfo {
  value: string;
  label: string;
  description: string;
}

export interface JobExecution {
  executionId: number;
  jobId: number;
  startZeit: string;
  endZeit?: string;
  dauerMs?: number;
  status: string;
  ergebnis?: string;
  fehlerMeldung?: string;
  verarbeiteteElemente?: number;
}

export interface CreateScheduledJobDto {
  jobName: string;
  jobTyp: string;
  beschreibung?: string;
  intervallMinuten: number;
  startzeit?: string; // Format "HH:mm" - Uhrzeit für erste Ausführung
  konfiguration?: string;
  istAktiv: boolean;
}

export interface UpdateScheduledJobDto {
  jobName?: string;
  jobTyp?: string;
  beschreibung?: string;
  intervallMinuten?: number;
  startzeit?: string; // Format "HH:mm" - Uhrzeit für erste Ausführung
  konfiguration?: string;
  istAktiv?: boolean;
}

export const scheduledJobsApi = {
  getAll: () => api.get<ScheduledJob[]>('/scheduledjobs'),
  getById: (id: number) => api.get<ScheduledJob>(`/scheduledjobs/${id}`),
  create: (data: CreateScheduledJobDto) => api.post<ScheduledJob>('/scheduledjobs', data),
  update: (id: number, data: UpdateScheduledJobDto) => api.put<ScheduledJob>(`/scheduledjobs/${id}`, data),
  delete: (id: number) => api.delete(`/scheduledjobs/${id}`),
  toggle: (id: number) => api.post<ScheduledJob>(`/scheduledjobs/${id}/toggle`, {}),
  runNow: (id: number) => api.post<JobExecution>(`/scheduledjobs/${id}/run`, {}),
  getExecutions: (id: number, limit?: number) =>
    api.get<JobExecution[]>(`/scheduledjobs/${id}/executions${limit ? `?limit=${limit}` : ''}`),
  getTypes: () => api.get<JobTypeInfo[]>('/scheduledjobs/types'),
};

// Mitteilungen API
export interface MitteilungenParams {
  page?: number;
  pageSize?: number;
  nurUngelesen?: boolean;
}

export const mitteilungenApi = {
  // Benutzer-Endpunkte
  getAll: (params?: MitteilungenParams) =>
    api.get('/mitteilungen', { params }),
  getById: (id: number) => api.get(`/mitteilungen/${id}`),
  getUngeleseneAnzahl: () => api.get<number>('/mitteilungen/ungelesen/anzahl'),
  alsGelesenMarkieren: (id: number) =>
    api.post(`/mitteilungen/${id}/gelesen`),
  alleAlsGelesenMarkieren: () =>
    api.post('/mitteilungen/alle-gelesen'),
  verstecken: (id: number) =>
    api.post(`/mitteilungen/${id}/verstecken`),

  // Admin-Endpunkte
  getAlleAdmin: (params?: { page?: number; pageSize?: number }) =>
    api.get('/mitteilungen/admin', { params }),
  getStatistik: () => api.get('/mitteilungen/admin/statistik'),
  create: (data: any) => api.post('/mitteilungen', data),
  update: (id: number, data: any) => api.put(`/mitteilungen/${id}`, data),
  delete: (id: number) => api.delete(`/mitteilungen/${id}`),

  // Release-Notes
  getReleaseNotes: () => api.get('/mitteilungen/release-notes'),
  getAlleReleaseNotes: () => api.get('/mitteilungen/release-notes/admin'),
  createReleaseNote: (data: any) => api.post('/mitteilungen/release-notes', data),
  updateReleaseNote: (id: number, data: any) =>
    api.put(`/mitteilungen/release-notes/${id}`, data),
  deleteReleaseNote: (id: number) =>
    api.delete(`/mitteilungen/release-notes/${id}`),
};

// Fristen API
export const fristenApi = {
  getAll: (params?: { adresseId?: number; status?: string; nurUeberfaellig?: boolean; nurDieseWoche?: boolean }) =>
    api.get<Frist[]>('/fristen', { params }),
  getById: (id: number) => api.get<Frist>(`/fristen/${id}`),
  getStatistik: (adresseId?: number) =>
    api.get<FristStatistik>('/fristen/statistik', { params: adresseId ? { adresseId } : undefined }),
  getKategorien: () => api.get<string[]>('/fristen/kategorien'),
  getErinnerungen: () => api.get<Frist[]>('/fristen/erinnerungen'),
  create: (data: CreateFristDto) => api.post<Frist>('/fristen', data),
  update: (id: number, data: UpdateFristDto) => api.put<Frist>(`/fristen/${id}`, data),
  markAsErledigt: (id: number) => api.put<Frist>(`/fristen/${id}/erledigt`),
  delete: (id: number) => api.delete(`/fristen/${id}`),
};

// Zeiterfassung (Time Tracking) API
export const zeiterfassungApi = {
  getAll: (params?: ZeiterfassungFilter) =>
    api.get<Zeiterfassung[]>('/zeiterfassung', { params }),
  getById: (id: number) =>
    api.get<Zeiterfassung>(`/zeiterfassung/${id}`),
  getStatistik: (benutzerID?: number) =>
    api.get<ZeiterfassungStatistik>('/zeiterfassung/statistik', { params: benutzerID ? { benutzerID } : undefined }),
  create: (data: CreateZeiterfassungDto) =>
    api.post<Zeiterfassung>('/zeiterfassung', data),
  update: (id: number, data: UpdateZeiterfassungDto) =>
    api.put<Zeiterfassung>(`/zeiterfassung/${id}`, data),
  delete: (id: number) =>
    api.delete(`/zeiterfassung/${id}`),
  freigeben: (ids: number[]) =>
    api.post<{ message: string; count: number }>('/zeiterfassung/freigeben', { ids }),
  zuruecksetzen: (id: number) =>
    api.post<Zeiterfassung>(`/zeiterfassung/zuruecksetzen/${id}`),
};

// Abrechnung (Billing/Invoicing) API
export const abrechnungApi = {
  getAll: (params?: AbrechnungFilter) =>
    api.get<Abrechnung[]>('/abrechnung', { params }),
  getById: (id: number) =>
    api.get<Abrechnung>(`/abrechnung/${id}`),
  getStatistik: () =>
    api.get<AbrechnungStatistik>('/abrechnung/statistik'),
  getNaechsteNummer: () =>
    api.get<NaechsteRechnungsnummer>('/abrechnung/naechste-nummer'),
  create: (data: CreateAbrechnungDto) =>
    api.post<Abrechnung>('/abrechnung', data),
  update: (id: number, data: UpdateAbrechnungDto) =>
    api.put<Abrechnung>(`/abrechnung/${id}`, data),
  delete: (id: number) =>
    api.delete(`/abrechnung/${id}`),
  versenden: (id: number) =>
    api.post<Abrechnung>(`/abrechnung/${id}/versenden`),
  bezahlt: (id: number, datum?: string) =>
    api.post<Abrechnung>(`/abrechnung/${id}/bezahlt`, datum ? { datum } : {}),
  stornieren: (id: number, notiz?: string) =>
    api.post<Abrechnung>(`/abrechnung/${id}/stornieren`, notiz ? { notiz } : {}),
  generateDokument: (id: number) =>
    api.post<{ dokumentID: number; dateiPfad: string; betreff: string }>(`/abrechnung/${id}/generate-dokument`),
  downloadPdf: (id: number) =>
    api.get(`/abrechnung/${id}/download-pdf`, { responseType: 'blob' }),
  sendEmail: (id: number, data: SendRechnungEmailDto) =>
    api.post<{ message: string; versendetAn: string }>(`/abrechnung/${id}/send-email`, data),
};

// Snipe-IT Asset Management API
export interface SnipeITAsset {
  id: number;
  name: string;
  assetTag: string;
  serial?: string;
  model?: {
    id: number;
    name: string;
  };
  modelNumber?: string;
  statusLabel?: {
    id: number;
    name: string;
    statusType: string;
    statusMeta: string;
  };
  category?: {
    id: number;
    name: string;
  };
  manufacturer?: {
    id: number;
    name: string;
  };
  location?: {
    id: number;
    name: string;
  };
  assignedTo?: {
    id: number;
    name: string;
    type: string;
  };
  purchaseDate?: {
    datetime: string;
    formatted: string;
  };
  purchaseCost?: string;
  warrantyMonths?: number;
  notes?: string;
  createdAt?: {
    datetime: string;
    formatted: string;
  };
  updatedAt?: {
    datetime: string;
    formatted: string;
  };
  userCanCheckout?: boolean;
}

export interface SnipeITLicense {
  id: number;
  name: string;
  company?: {
    id: number;
    name: string;
  };
  manufacturer?: {
    id: number;
    name: string;
  };
  productKey?: string;
  orderNumber?: string;
  purchaseOrder?: string;
  purchaseDate?: {
    datetime: string;
    formatted: string;
  };
  purchaseCost?: string;
  notes?: string;
  expirationDate?: {
    datetime: string;
    formatted: string;
  };
  seats: number;
  freeSeatsCount: number;
  licenseName?: string;
  licenseEmail?: string;
  maintained?: boolean;
}

export interface SnipeITAccessory {
  id: number;
  name: string;
  company?: {
    id: number;
    name: string;
  };
  manufacturer?: {
    id: number;
    name: string;
  };
  category?: {
    id: number;
    name: string;
  };
  location?: {
    id: number;
    name: string;
  };
  modelNumber?: string;
  qty: number;
  remainingQty: number;
  purchaseDate?: {
    datetime: string;
    formatted: string;
  };
  purchaseCost?: string;
  orderNumber?: string;
  notes?: string;
}

export interface SnipeITConsumable {
  id: number;
  name: string;
  company?: {
    id: number;
    name: string;
  };
  manufacturer?: {
    id: number;
    name: string;
  };
  category?: {
    id: number;
    name: string;
  };
  location?: {
    id: number;
    name: string;
  };
  modelNumber?: string;
  itemNo?: string;
  qty: number;
  remaining: number;
  purchaseDate?: {
    datetime: string;
    formatted: string;
  };
  purchaseCost?: string;
  orderNumber?: string;
  notes?: string;
}

export interface SnipeITUser {
  id: number;
  avatar?: string;
  name: string;
  firstName: string;
  lastName?: string;
  username: string;
  email?: string;
  employeeNum?: string;
  department?: {
    id: number;
    name: string;
  };
  location?: {
    id: number;
    name: string;
  };
  assetsCount: number;
  licensesCount: number;
  accessoriesCount: number;
  consumablesCount: number;
}

export interface SnipeITStatusLabel {
  id: number;
  name: string;
  statusType: string;
  statusMeta?: string;
  color?: string;
  notes?: string;
  assetsCount: number;
}

export interface SnipeITCategory {
  id: number;
  name: string;
  categoryType: string;
  assetsCount: number;
  accessoriesCount: number;
  consumablesCount: number;
  licensesCount: number;
}

export interface SnipeITLocation {
  id: number;
  name: string;
  address?: string;
  address2?: string;
  city?: string;
  state?: string;
  country?: string;
  zip?: string;
  assetsCount: number;
  usersCount: number;
}

export interface SnipeITActivityLog {
  id: number;
  admin?: {
    id: number;
    name: string;
  };
  actionType: string;
  item?: {
    id: number;
    type: string;
    name: string;
  };
  target?: {
    id: number;
    type: string;
    name: string;
  };
  note?: string;
  createdAt: {
    datetime: string;
    formatted: string;
  };
}

export interface SnipeITListResponse<T> {
  total: number;
  rows: T[];
}

export interface SnipeITCheckoutRequest {
  checkoutToType: 'user' | 'asset' | 'location';
  assignedUser?: number;
  assignedAsset?: number;
  assignedLocation?: number;
  expectedCheckin?: string;
  checkoutAt?: string;
  name?: string;
  note?: string;
}

export interface SnipeITCheckinRequest {
  note?: string;
  name?: string;
  locationId?: number;
}

export interface SnipeITLicenseSeatCheckoutRequest {
  assignedTo?: number;
  assetId?: number;
  note?: string;
}

export interface SnipeITAccessoryCheckoutRequest {
  assignedTo: number;
  note?: string;
}

export interface SnipeITConsumableCheckoutRequest {
  assignedTo: number;
  note?: string;
}

export interface SnipeITLicenseSeat {
  id: number;
  licenseId: number;
  assignedUser?: {
    id: number;
    name: string;
  };
  assignedAsset?: {
    id: number;
    name: string;
  };
  location?: {
    id: number;
    name: string;
  };
  reassignable: boolean;
  name?: string;
}

export const snipeITApi = {
  // Status
  getStatus: () => api.get<{ configured: boolean }>('/snipeit/status'),

  // Assets
  getAssets: (params?: { limit?: number; offset?: number; search?: string; status?: string; statusId?: number }) =>
    api.get<SnipeITListResponse<SnipeITAsset>>('/snipeit/assets', { params }),
  getAssetById: (id: number) => api.get<SnipeITAsset>(`/snipeit/assets/${id}`),
  getAssetByTag: (tag: string) => api.get<SnipeITAsset>(`/snipeit/assets/bytag/${tag}`),
  checkoutAsset: (id: number, data: SnipeITCheckoutRequest) =>
    api.post(`/snipeit/assets/${id}/checkout`, data),
  checkinAsset: (id: number, data?: SnipeITCheckinRequest) =>
    api.post(`/snipeit/assets/${id}/checkin`, data || {}),

  // Licenses
  getLicenses: (params?: { limit?: number; offset?: number; search?: string }) =>
    api.get<SnipeITListResponse<SnipeITLicense>>('/snipeit/licenses', { params }),
  getLicenseById: (id: number) => api.get<SnipeITLicense>(`/snipeit/licenses/${id}`),
  getLicenseSeats: (licenseId: number) =>
    api.get<SnipeITListResponse<SnipeITLicenseSeat>>(`/snipeit/licenses/${licenseId}/seats`),
  checkoutLicenseSeat: (licenseId: number, seatId: number, data: SnipeITLicenseSeatCheckoutRequest) =>
    api.post(`/snipeit/licenses/${licenseId}/seats/${seatId}/checkout`, data),
  checkinLicenseSeat: (licenseId: number, seatId: number) =>
    api.post(`/snipeit/licenses/${licenseId}/seats/${seatId}/checkin`, {}),

  // Accessories
  getAccessories: (params?: { limit?: number; offset?: number; search?: string }) =>
    api.get<SnipeITListResponse<SnipeITAccessory>>('/snipeit/accessories', { params }),
  getAccessoryById: (id: number) => api.get<SnipeITAccessory>(`/snipeit/accessories/${id}`),
  checkoutAccessory: (id: number, data: SnipeITAccessoryCheckoutRequest) =>
    api.post(`/snipeit/accessories/${id}/checkout`, data),
  checkinAccessory: (id: number, assignedPivotId: number) =>
    api.post(`/snipeit/accessories/${id}/checkin`, { assignedPivotId }),

  // Consumables
  getConsumables: (params?: { limit?: number; offset?: number; search?: string }) =>
    api.get<SnipeITListResponse<SnipeITConsumable>>('/snipeit/consumables', { params }),
  getConsumableById: (id: number) => api.get<SnipeITConsumable>(`/snipeit/consumables/${id}`),
  checkoutConsumable: (id: number, data: SnipeITConsumableCheckoutRequest) =>
    api.post(`/snipeit/consumables/${id}/checkout`, data),

  // Users
  getUsers: (params?: { limit?: number; offset?: number; search?: string }) =>
    api.get<SnipeITListResponse<SnipeITUser>>('/snipeit/users', { params }),
  getUserById: (id: number) => api.get<SnipeITUser>(`/snipeit/users/${id}`),
  getUserAssets: (id: number) => api.get<SnipeITListResponse<SnipeITAsset>>(`/snipeit/users/${id}/assets`),

  // Lookups
  getStatusLabels: () => api.get<SnipeITListResponse<SnipeITStatusLabel>>('/snipeit/statuslabels'),
  getCategories: () => api.get<SnipeITListResponse<SnipeITCategory>>('/snipeit/categories'),
  getLocations: () => api.get<SnipeITListResponse<SnipeITLocation>>('/snipeit/locations'),

  // Activity
  getActivityLog: (params?: { limit?: number; offset?: number; assetId?: number }) =>
    api.get<SnipeITListResponse<SnipeITActivityLog>>('/snipeit/activity', { params }),
};

export default api;
